/**
 * parseCrearTarea — interpreta un texto libre del ⌘K y deduce los datos de una tarea nueva
 * (brand book SOA 10 PRO §8): cliente, tipo, fecha, responsable y créditos. No crea nada: sirve para
 * precompletar CrearTareaModal (Enter abre el modal; nunca crea en silencio).
 *
 *   "reel bernardo viernes @paz"  →  { cliente: Bernardo, tipo: "Reel", fecha: "2026-10-16", responsable: Paz, titulo: "…" }
 */
const sinTildes = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const TIPOS = [
  ["Reel", /\b(reel|reels|video|videos|tiktok)\b/],
  ["Historia", /\b(historia|historias|story|stories)\b/],
  ["Carrusel", /\b(carrusel|carousel)\b/],
  ["Gráfica", /\b(grafica|graficas|arte|artes|diseno|post|flyer|placa|banner)\b/],
  ["Fotos", /\b(foto|fotos|fotografia|sesion)\b/],
  ["Cobertura", /\b(cobertura|coberturas|evento)\b/],
  ["Presentación", /\b(presentacion|deck|ppt)\b/],
  ["Documento", /\b(documento|informe|reporte|brief)\b/],
];
const DIAS = { domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6 };

const iso = (d) => d.toISOString().slice(0, 10);
const hoyPY = (ahora) => new Date(new Date(ahora.getTime() - 3 * 3600 * 1000).toISOString().slice(0, 10) + "T12:00:00Z");

export function parseFecha(texto, ahora = new Date()) {
  const t = sinTildes(texto);
  const base = hoyPY(ahora);
  const sumar = (n) => { const d = new Date(base); d.setUTCDate(d.getUTCDate() + n); return d; };
  let m;
  if (/\bpasado manana\b/.test(t)) return { fecha: iso(sumar(2)), usado: /\bpasado manana\b/ };
  if (/\bmanana\b/.test(t)) return { fecha: iso(sumar(1)), usado: /\bmanana\b/ };
  if (/\bhoy\b/.test(t)) return { fecha: iso(sumar(0)), usado: /\bhoy\b/ };
  if ((m = t.match(/\b(\d{1,2})[\/-](\d{1,2})(?:[\/-](\d{2,4}))?\b/))) {
    const dd = +m[1], mm = +m[2], yy = m[3] ? (+m[3] < 100 ? 2000 + +m[3] : +m[3]) : base.getUTCFullYear();
    if (dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12) return { fecha: `${yy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`, usado: /\b\d{1,2}[\/-]\d{1,2}(?:[\/-]\d{2,4})?\b/ };
  }
  for (const [nombre, dow] of Object.entries(DIAS)) {
    const rx = new RegExp("\\b" + nombre + "\\b");
    if (rx.test(t)) {
      let falta = (dow - base.getUTCDay() + 7) % 7;
      if (falta === 0) falta = 7;          // «viernes» dicho un viernes = el próximo
      return { fecha: iso(sumar(falta)), usado: rx };
    }
  }
  return { fecha: null, usado: null };
}

export function parseCrearTarea(texto, { clientes = [], equipo = [], ahora = new Date() } = {}) {
  const original = String(texto || "").trim();
  const t = sinTildes(original);
  let resto = original;

  // responsable: @nombre
  let responsable = null;
  const mArr = t.match(/@([a-z0-9._-]+)/);
  if (mArr) {
    const q = mArr[1];
    responsable = equipo.find(p => sinTildes(p.nombre).split(/\s+/).some(w => w.startsWith(q)) || sinTildes(p.email || "").startsWith(q)) || null;
    resto = resto.replace(/@\S+/, " ");
  }

  // cliente: el cliente cuyo nombre (o su primera palabra significativa) aparece en el texto
  let cliente = null, mejor = 0;
  for (const c of clientes) {
    const n = sinTildes(c.nombre).replace(/[^a-z0-9 ]/g, " ").trim();
    if (!n) continue;
    const palabras = n.split(/\s+/).filter(w => w.length >= 3);
    let puntaje = 0;
    if (new RegExp("\\b" + n.replace(/ /g, "\\s+") + "\\b").test(t)) puntaje = 100 + n.length;
    else for (const w of palabras) if (new RegExp("\\b" + w + "\\b").test(t)) puntaje = Math.max(puntaje, w.length);
    if (puntaje > mejor) { mejor = puntaje; cliente = c; }
  }

  // tipo
  let tipo = null;
  for (const [nombre, rx] of TIPOS) if (rx.test(t)) { tipo = nombre; break; }

  // fecha
  const { fecha, usado } = parseFecha(original, ahora);
  if (usado) resto = sinTildes(resto).replace(usado, " ") && resto.replace(new RegExp(usado.source, "i"), " ");

  // título = lo que sobra, sin las palabras ya usadas para cliente/tipo
  const quitar = [];
  if (cliente) quitar.push(...sinTildes(cliente.nombre).split(/\s+/).filter(w => w.length >= 3));
  let titulo = resto;
  for (const w of quitar) titulo = titulo.replace(new RegExp("\\b" + w + "\\b", "ig"), " ");
  // se quita la palabra que delató el tipo y las palabras de enlace que quedan sueltas en los bordes
  const rxTipo = TIPOS.find(([n]) => n === tipo)?.[1];
  let palabras = titulo.split(/\s+/).filter(Boolean).filter(w => !(rxTipo && rxTipo.test(sinTildes(w))));
  const enlace = new Set(["la", "el", "los", "las", "de", "del", "para", "un", "una", "y", "para"]);
  while (palabras.length && enlace.has(sinTildes(palabras[0]))) palabras.shift();
  while (palabras.length && enlace.has(sinTildes(palabras[palabras.length - 1]))) palabras.pop();
  titulo = palabras.join(" ").trim();
  if (!titulo) titulo = original;

  return { titulo, cliente, tipo, fecha, responsable, creditos: 1 };
}
